import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

export type CardTone = 'white' | 'sage' | 'teal' | 'apricot' | 'provider' | 'lavender' | 'mint' | 'peach';
export type CardPadding = 'md' | 'lg';

const TONE_CLASS: Record<CardTone, string> = {
  white: 'bg-white',
  sage: 'bg-sage-100',
  teal: 'bg-teal-600 text-white',
  apricot: 'bg-apricot-200 text-apricot-ink',
  provider: 'bg-provider-soft text-provider-ink',
  lavender: 'bg-lavender-200 text-ink',
  mint: 'bg-mint-200 text-ink',
  peach: 'bg-peach-200 text-ink',
};

export type CardProps = {
  tone?: CardTone;
  padding?: CardPadding;
  as?: keyof JSX.IntrinsicElements;
  interactive?: boolean;
  children?: ReactNode;
  className?: string;
} & Omit<HTMLAttributes<HTMLElement>, 'className'>;

export const Card = ({
  tone = 'white',
  padding = 'md',
  as = 'div',
  interactive = false,
  className = '',
  children,
  ...rest
}: CardProps) => {
  // Polymorphic `as`: each intrinsic tag has its own attribute surface, so we
  // render dynamically and let callers pass tag-appropriate `...rest` props.
  const Tag = as as unknown as 'div';
  return (
    <Tag
      className={cx(
        'rounded-lg shadow-1',
        TONE_CLASS[tone],
        padding === 'lg' ? 'p-8' : 'p-5',
        interactive && 'cursor-pointer transition-shadow hover:shadow-2',
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
};
